import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { X, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../Auth/context/AuthContext';

const BidEditModal = ({ bid, onClose, onSave }) => {
  const { auth } = useContext(AuthContext);
  const isAdmin = auth.user_type === 'Organization';
  const STATUSES = ['open', 'won', 'lost', 'archived'];
  const [form, setForm] = useState({
    name: '',
    client: '',
    dueDate: '',
    status: 'open',
  });
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ name: '', dueDate: '' });

  useEffect(() => {
    if (bid) {
      setForm({
        name: bid.name || '',
        client: bid.client || '',
        dueDate: bid.dueDate ? bid.dueDate.split('T')[0] : '',
        status: bid.status || 'open',
      });
    }
  }, [bid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required name
    if (!form.name.trim()) {
      setFieldErrors(f => ({ ...f, name: 'Name is required' }));
      return;
    }
    // Validate dueDate format if present
    if (isAdmin && form.dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(form.dueDate)) {
      setFieldErrors(f => ({ ...f, dueDate: 'Invalid date format' }));
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      const payload = { name: form.name };
      if (isAdmin) {
        payload.client = form.client;
        payload.dueDate = form.dueDate;
        payload.status = form.status;
      }
      const resp = await axios.put(
        `/api/myteconstruction/bids/${bid._id}`,
        payload,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      onSave(resp.data);
      toast.success('Bid updated successfully');
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || 'Failed to update bid';
      setServerError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!bid) return null;
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="edit-bid-title" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-gray-900 text-white p-6 rounded-lg w-full max-w-md mx-2 sm:mx-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-200"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 id="edit-bid-title" className="text-xl font-bold mb-4">Edit Bid</h2>
        {serverError && <p role="alert" className="text-red-500 mb-2">{serverError}</p>}
        <form onSubmit={handleSubmit}>
          <label htmlFor="edit-bid-name" className="block mb-2">
            Bid Name <span className="text-red-500">*</span>
          </label>
          <input
            id="edit-bid-name"
            type="text"
            aria-required="true"
            aria-invalid={fieldErrors.name ? 'true' : 'false'}
            aria-describedby={fieldErrors.name ? 'edit-bid-name-error' : undefined}
            className="w-full p-2 mb-1 bg-gray-800 rounded"
            value={form.name}
            onChange={(e) => {
              setForm(f => ({ ...f, name: e.target.value }));
              if (fieldErrors.name) setFieldErrors(f => ({ ...f, name: '' }));
            }}
            onBlur={() => {
              if (!form.name.trim()) {
                setFieldErrors(f => ({ ...f, name: 'Name is required' }));
              }
            }}
            disabled={submitting}
            autoFocus
          />
          {fieldErrors.name && (
            <p id="edit-bid-name-error" role="alert" className="text-red-500 text-sm mb-2">
              {fieldErrors.name}
            </p>
          )}
          {isAdmin && (
            <>
              <label htmlFor="edit-bid-client" className="block mb-2">Client (optional)</label>
              <input
                id="edit-bid-client"
                type="text"
                className="w-full p-2 mb-4 bg-gray-800 rounded"
                value={form.client}
                onChange={(e) => setForm(f => ({ ...f, client: e.target.value }))}
                disabled={submitting}
              />
              <label htmlFor="edit-bid-duedate" className="block mb-2">Due Date (optional)</label>
              <input
                id="edit-bid-duedate"
                type="date"
                aria-invalid={fieldErrors.dueDate ? 'true' : 'false'}
                aria-describedby={fieldErrors.dueDate ? 'edit-bid-duedate-error' : undefined}
                className="w-full p-2 mb-1 bg-gray-800 rounded"
                value={form.dueDate}
                onChange={(e) => {
                  setForm(f => ({ ...f, dueDate: e.target.value }));
                  if (fieldErrors.dueDate) setFieldErrors(f => ({ ...f, dueDate: '' }));
                }}
                onBlur={() => {
                  if (form.dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(form.dueDate)) {
                    setFieldErrors(f => ({ ...f, dueDate: 'Invalid date format' }));
                  }
                }}
                disabled={submitting}
              />
              {fieldErrors.dueDate && (
                <p id="edit-bid-duedate-error" role="alert" className="text-red-500 text-sm mb-2">
                  {fieldErrors.dueDate}
                </p>
              )}
              <label htmlFor="edit-bid-status" className="block mb-2">Status (optional)</label>
              <select
                id="edit-bid-status"
                className="w-full p-2 mb-4 bg-gray-800 rounded"
                value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                disabled={submitting}
              >
                {STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </>
          )}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 rounded hover:bg-gray-700"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center px-4 py-2 bg-blue-600 rounded hover:bg-blue-700"
              disabled={submitting}
            >
              <Check className="h-4 w-4 mr-1" />
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BidEditModal;